import {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Button, Modal, Form, Table, Badge, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {panaAdmin} from '../../../../../services/pana'

const BukuPanduanAdmin: FC = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [data, setData] = useState<any[]>([])
  const [formData, setFormData] = useState<any>({
    id: null,
    nama_dokumen: '',
    tipe_dokumen: '',
    is_active: 1,
    file: null,
  })
  const [editMode, setEditMode] = useState<boolean>(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await panaAdmin.getBukuPanduanListHash()
      setData(response.data.data || [])
    } catch (error: any) {
      toast.error('Gagal memuat data: ' + (error.response?.data?.message || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = (item?: any) => {
    if (item) {
      setEditMode(true)
      setFormData({
        id: item.id,
        nama_dokumen: item.nama_dokumen,
        tipe_dokumen: item.tipe_dokumen || '',
        is_active: item.is_active,
        file: null,
      })
    } else {
      setEditMode(false)
      setFormData({
        id: null,
        nama_dokumen: '',
        tipe_dokumen: '',
        is_active: 1,
        file: null,
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setFormData({
      id: null,
      nama_dokumen: '',
      tipe_dokumen: '',
      is_active: 1,
      file: null,
    })
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]

      // Validate file type
      if (!file.name.endsWith('.pdf')) {
        toast.error('File harus berformat PDF')
        return
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Ukuran file maksimal 10MB')
        return
      }

      setFormData({...formData, file})
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.nama_dokumen) {
      toast.error('Nama dokumen harus diisi')
      return
    }

    if (!editMode && !formData.file) {
      toast.error('File harus diupload')
      return
    }

    setLoading(true)

    try {
      const payload = new FormData()
      payload.append('nama_dokumen', formData.nama_dokumen)
      payload.append('tipe_dokumen', formData.tipe_dokumen)
      payload.append('is_active', formData.is_active.toString())
      if (formData.file) {
        payload.append('file', formData.file)
      }

      if (editMode && formData.id) {
        await panaAdmin.updateBukuPanduan(formData.id, payload)
        toast.success('Berhasil mengupdate buku panduan')
      } else {
        await panaAdmin.createBukuPanduan(payload)
        toast.success('Berhasil menambahkan buku panduan')
      }

      handleCloseModal()
      fetchData()
    } catch (error: any) {
      toast.error(
        'Gagal menyimpan data: ' + (error.response?.data?.meta?.message || error.message)
      )
    } finally {
      setLoading(false)
    }
  }

  const handleToggleActive = async (item: any) => {
    setLoading(true)
    try {
      const payload = new FormData()
      payload.append('nama_dokumen', formData.nama_dokumen)
      payload.append('tipe_dokumen', formData.tipe_dokumen || '')
      payload.append('is_active', item.is_active === 1 ? '0' : '1')

      await panaAdmin.updateBukuPanduan(item.id, payload)
      toast.success('Status berhasil diubah')
      fetchData()
    } catch (error: any) {
      toast.error('Gagal mengubah status: ' + (error.response?.data?.meta?.message || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async (item: any) => {
    try {
      const response = await panaAdmin.downloadBukuPanduan(item.tipe_dokumen)
      
      const fileUrl = response.url || response.file_path
      
      if (fileUrl) {
        window.open(fileUrl, '_blank')
        toast.success('File berhasil dibuka')
      } else {
        toast.error('URL file tidak ditemukan')
      }
    } catch (error: any) {
      console.error('Error downloading file:', error)
      toast.error(error?.response?.data?.message || 'Gagal mendownload file')
    }
  }

  const handleDelete = async (id: any) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus buku panduan ini?')) {
      return
    }

    setLoading(true)
    try {
      await panaAdmin.deleteBukuPanduan(id)
      toast.success('Berhasil menghapus buku panduan')
      fetchData()
    } catch (error: any) {
      toast.error('Gagal menghapus data: ' + (error.response?.data?.message || error.message))
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Kelola Buku Panduan - Admin</PageTitle>
      <div className='card'>
        <div className='card-header border-0 pt-6'>
          <div className='card-title'>
            <h3 className='fw-bold m-0'>Kelola Buku Panduan</h3>
          </div>
          <div className='card-toolbar'>
            <Button variant='primary' size='sm' onClick={() => handleOpenModal()}>
              <i className='bi bi-plus-circle me-2'></i>
              Tambah Buku Panduan
            </Button>
          </div>
        </div>

        <div className='card-body py-4'>
          {loading && (
            <div className='text-center py-5'>
              <Spinner animation='border' variant='primary' />
            </div>
          )}

          {!loading && (
            <div className='table-responsive'>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th style={{width: '50px'}}>No</th>
                    <th>Nama Dokumen</th>
                    <th style={{width: '120px'}}>Tipe</th>
                    <th style={{width: '100px'}}>Status</th>
                    <th style={{width: '150px'}}>Dibuat</th>
                    <th style={{width: '150px'}}>Diupdate</th>
                    <th style={{width: '200px'}}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan={7} className='text-center text-muted'>
                        Belum ada data
                      </td>
                    </tr>
                  ) : (
                    data.map((item, index) => (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        <td>{item.nama_dokumen}</td>
                        <td>
                          <Badge bg='primary' className='text-uppercase'>
                            {item.tipe_dokumen || '-'}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={item.is_active === 1 ? 'success' : 'secondary'}>
                            {item.is_active === 1 ? 'Aktif' : 'Nonaktif'}
                          </Badge>
                        </td>
                        <td className='text-muted small'>
                          {new Date(item.created_at).toLocaleDateString('id-ID', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </td>
                        <td className='text-muted small'>
                          {new Date(item.updated_at).toLocaleDateString('id-ID', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </td>
                        <td>
                          <Button
                            variant='info'
                            size='sm'
                            className='me-2'
                            onClick={() => handleDownload(item)}
                            title='Lihat File'
                          >
                            <i className='bi bi-eye'></i>
                          </Button>
                          <Button
                            variant='warning'
                            size='sm'
                            className='me-2'
                            onClick={() => handleOpenModal(item)}
                            title='Edit'
                          >
                            <i className='bi bi-pencil'></i>
                          </Button>
                          <Button
                            variant={item.is_active === 1 ? 'secondary' : 'success'}
                            size='sm'
                            className='me-2'
                            onClick={() => handleToggleActive(item)}
                            title={item.is_active === 1 ? 'Nonaktifkan' : 'Aktifkan'}
                          >
                            <i
                              className={item.is_active === 1 ? 'bi bi-toggle-on' : 'bi bi-toggle-off'}
                            ></i>
                          </Button>
                          <Button
                            variant='danger'
                            size='sm'
                            onClick={() => handleDelete(item.id)}
                            title='Hapus'
                          >
                            <i className='bi bi-trash'></i>
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Create/Edit */}
      <Modal show={showModal} onHide={handleCloseModal} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>{editMode ? 'Edit' : 'Tambah'} Buku Panduan</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className='mb-3'>
              <Form.Label>
                Nama Dokumen <span className='text-danger'>*</span>
              </Form.Label>
              <Form.Control
                type='text'
                placeholder='Masukkan nama dokumen'
                value={formData.nama_dokumen}
                onChange={(e) => setFormData({...formData, nama_dokumen: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>
                Tipe Dokumen <span className='text-danger'>*</span>
              </Form.Label>
              <Form.Control
                type='text'
                placeholder='Contoh: wawancara, substansi, administrasi'
                value={formData.tipe_dokumen}
                onChange={(e) => setFormData({...formData, tipe_dokumen: e.target.value})}
                required
              />
              <Form.Text className='text-muted'>
                Masukkan tipe dokumen sesuai dengan penempatannya
              </Form.Text>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>
                Upload File PDF {!editMode && <span className='text-danger'>*</span>}
              </Form.Label>
              <Form.Control type='file' accept='.pdf' onChange={handleFileChange} />
              <Form.Text className='text-muted'>
                Format: PDF, Maksimal ukuran: 10MB
                {editMode && ' (Kosongkan jika tidak ingin mengubah file)'}
              </Form.Text>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Check
                type='switch'
                id='is-active-switch'
                label='Aktifkan dokumen (tampilkan ke user)'
                checked={formData.is_active === 1}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked ? 1 : 0})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={handleCloseModal} disabled={loading}>
              Batal
            </Button>
            <Button variant='primary' type='submit' disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation='border' size='sm' className='me-2' />
                  Menyimpan...
                </>
              ) : (
                'Simpan'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}

export default BukuPanduanAdmin
